import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { REGISTER_CARDS, type RegisterCardKey } from './registerCards';

/** Permanenza su ogni card prima del crossfade (come il carosello del sito). */
const AUTOPLAY_MS = 6000;
const FADE_MS = 900;

export interface RegisterCardCarouselProps {
  /** Card mostrata per prima (poi si prosegue in ordine, in loop). */
  startCard: RegisterCardKey;
  language?: 'it' | 'en';
}

/**
 * Pannello destro del flusso registrazione: le stesse card del carosello
 * funzionalità del sito. Copy in alto a sinistra; screenshot ancorato in alto
 * a sinistra dell'area shot che sborda (croppato) in basso a destra, senza
 * frame né sfondo bianco — identico al ritaglio ".shot.v2" del sito.
 * Crossfade automatico tra le card; fermo su prefers-reduced-motion.
 */
export function RegisterCardCarousel({ startCard, language = 'it' }: RegisterCardCarouselProps) {
  const startIndex = useMemo(() => {
    const i = REGISTER_CARDS.findIndex((c) => c.key === startCard);
    return i >= 0 ? i : 0;
  }, [startCard]);

  const [active, setActive] = useState(startIndex);
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(mq.matches);
    const onChange = (e: MediaQueryListEvent) => setReduced(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    if (reduced) return;
    const id = window.setInterval(() => {
      setActive((a) => (a + 1) % REGISTER_CARDS.length);
    }, AUTOPLAY_MS);
    return () => window.clearInterval(id);
  }, [reduced]);

  return (
    <div className="relative w-full h-full overflow-hidden">
      {REGISTER_CARDS.map((card, i) => {
        const copy = card[language];
        const isActive = i === active;
        return (
          <div
            key={card.key}
            aria-hidden={!isActive}
            className="absolute inset-0 transition-opacity ease-in-out"
            style={{
              opacity: isActive ? 1 : 0,
              transitionDuration: `${FADE_MS}ms`,
              pointerEvents: isActive ? 'auto' : 'none',
            }}
          >
            {/* Copy della card (stesse frasi del sito); key={language} → fade al cambio lingua */}
            <motion.div
              key={language}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
              className="absolute top-0 left-0 right-0 px-10 xl:px-14 pt-14 xl:pt-16 z-10"
            >
              <div className="flex items-center gap-2 text-[11px] font-semibold tracking-[0.14em] text-[#787774]">
                <span
                  className="inline-block w-2 h-2 rounded-full shrink-0"
                  style={{ background: 'linear-gradient(135deg,#f52ba7,#ff8a3d)' }}
                />
                {copy.kicker}
              </div>
              <h3 className="mt-3 text-[26px] xl:text-[32px] font-extrabold leading-[1.12] tracking-[-0.02em] text-[#37352F] [text-wrap:balance] max-w-[520px]">
                {copy.title}
              </h3>
              <p className="mt-3 text-[15px] xl:text-base leading-relaxed text-[#787774] max-w-[420px]">
                {copy.desc}
              </p>
            </motion.div>

            {/* Screenshot ancorato in basso a destra A SCALA NATURALE (le webp
                sono 2x per retina): mai object-cover — su pannelli grandi
                zoomerebbe il testo oltre la risoluzione nativa rendendolo
                illeggibile. Il device è già ritagliato dentro l'immagine,
                quindi appoggiata all'angolo dà lo stesso effetto "croppato"
                delle card del sito. */}
            <div className="absolute left-8 right-0 top-[230px] xl:top-[250px] bottom-0 flex items-end justify-end">
              <img
                src={card.src}
                alt={card.alt}
                className="w-auto h-auto max-h-full object-contain object-right-bottom"
                style={{
                  maxWidth: 'min(83%, 650px)',
                  filter: 'drop-shadow(0 26px 46px rgba(40,30,50,0.20))',
                }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}
